import {
  horizontalScrollThumbMetrics,
  scrollLeftFromThumbLeft,
} from '@client/helpers/fade-scrollbar.helpers';
import {
  getMainHorizontalScrollTarget,
  subscribeMainHorizontalScrollTarget,
} from '@client/helpers/main-horizontal-scroll.store';
import { useHoverWithRefCallback } from '@client/helpers/dom.helpers';
import { Box, SxProps, Theme } from '@mui/material';
import { mergeSx } from '@client/helpers/mui.helpers';
import { makeStyles } from 'tss-react/mui';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  PointerEvent as ReactPointerEvent,
  PointerEventHandler,
  RefObject,
} from 'react';

/** Hit-area width for a custom scrollbar rail. */
const SCROLLBAR_RAIL_WIDTH_PX = 12;
/** Right-edge hover zone that reveals the vertical scrollbar. */
const VERTICAL_SCROLLBAR_HOVER_ZONE_PX = 20;
/** Visible thumb thickness inside the rail. */
const SCROLLBAR_THUMB_THICKNESS_PX = 5;
/** Fade-out delay after scroll / pointer leave. */
const SCROLLBAR_HIDE_MS = 800;
/** Shortest the thumb may get along the scroll axis. */
const SCROLLBAR_THUMB_MIN_LENGTH_PX = 28;

type VerticalThumbMetrics = {
  top: number;
  height: number;
};

type HorizontalThumbMetrics = {
  left: number;
  width: number;
  /** Metadata pane start inside the rail (right of the frozen name column). */
  trackLeft: number;
};

type ScrollbarProps = {
  /** The scrolling element the rail drives. */
  scrollRef: RefObject<HTMLElement | null>;
  /** Positioned ancestor holding the rails; rail offsets are relative to it. */
  wrapperRef: RefObject<HTMLElement | null>;
};

const railSx: SxProps<Theme> = { pointerEvents: 'auto' };

const verticalRailSx = {
  position: 'absolute',
  right: 0,
  top: 0,
  bottom: 0,
  width: VERTICAL_SCROLLBAR_HOVER_ZONE_PX,
  // Overlay both Build sticky-header layers (3 and 4).
  zIndex: 5,
} as const;

const horizontalRailSx = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: SCROLLBAR_RAIL_WIDTH_PX,
  // Overlay both Build sticky-header layers (3 and 4).
  zIndex: 5,
} as const;

const useThumbStyles = makeStyles<{ visible: boolean }>()(
  (_theme, { visible }) => ({
    thumb: {
      borderRadius: 99,
      backgroundColor: 'rgba(0,0,0,0.2)',
      opacity: visible ? 1 : 0,
      // Hittable once shown; rail hover reveals it before grab.
      pointerEvents: visible ? 'auto' : 'none',
      cursor: 'default',
      transition: 'opacity 0.2s',
    },
  }),
);

type FadeVisibility = {
  visible: boolean;
  show: () => void;
  scheduleHide: () => void;
  isDraggingRef: RefObject<boolean>;
};

/**
 * Fade state for one rail: visible while scrolling, dragging or hovering, then
 * hidden again after {@link SCROLLBAR_HIDE_MS}.
 *
 * {@link isHoveredRef} mirrors reactive hover state rather than being written
 * by hand, so a rail that unmounts mid-hover cannot leave the thumb stuck on.
 */
const useFadeVisibility = (
  isHoveredRef: RefObject<boolean>,
): FadeVisibility => {
  const [visible, setVisible] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const isDraggingRef = useRef(false);

  const show = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    setVisible(true);
  }, []);

  /** Hide unless the user is mid-drag or still hovering. */
  const scheduleHide = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (isDraggingRef.current || isHoveredRef.current) return;
      setVisible(false);
    }, SCROLLBAR_HIDE_MS);
  }, [isHoveredRef]);

  useEffect(() => () => clearTimeout(hideTimerRef.current), []);

  return { visible, show, scheduleHide, isDraggingRef };
};

type ThumbDragHandlers = {
  onPointerDown: PointerEventHandler<HTMLDivElement>;
  onPointerMove: PointerEventHandler<HTMLDivElement>;
  onPointerUp: PointerEventHandler<HTMLDivElement>;
  onPointerCancel: PointerEventHandler<HTMLDivElement>;
};

/**
 * Pointer-capture drag plumbing for a thumb. {@link onGrab} records where
 * inside the thumb the pointer landed, {@link onDrag} applies each move.
 */
const useThumbDrag = (
  { isDraggingRef, show, scheduleHide }: FadeVisibility,
  onGrab: (e: ReactPointerEvent<HTMLDivElement>) => void,
  onDrag: (e: ReactPointerEvent<HTMLDivElement>) => void,
): ThumbDragHandlers => {
  const onPointerDown = useCallback<PointerEventHandler<HTMLDivElement>>(
    (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      isDraggingRef.current = true;
      show();
      onGrab(e);
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [isDraggingRef, show, onGrab],
  );

  const onPointerMove = useCallback<PointerEventHandler<HTMLDivElement>>(
    (e) => {
      if (!isDraggingRef.current) return;
      onDrag(e);
    },
    [isDraggingRef, onDrag],
  );

  const onPointerUp = useCallback<PointerEventHandler<HTMLDivElement>>(
    (e) => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      scheduleHide();
    },
    [isDraggingRef, scheduleHide],
  );

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
  };
};

type RailFade = FadeVisibility & {
  /** Attach to the rail element to track hover over the hit-area. */
  railHoverRef: (element: HTMLDivElement | null) => void;
};

/**
 * Reveal rules for a rail. Horizontal scrolling keeps its broad content-hover
 * affordance; the vertical rail opts out so it appears only inside its narrow
 * right-edge hover zone (or while scrolling/dragging).
 */
const useRailFade = (
  scrollRef: RefObject<HTMLElement | null>,
  updateThumb: () => void,
  revealOnContentHover = true,
): RailFade => {
  const [isRailHovered, railHoverRef] =
    useHoverWithRefCallback<HTMLDivElement>();
  const [isContentHovered, contentHoverRef] =
    useHoverWithRefCallback<HTMLElement>();

  const isHovered = isRailHovered || (revealOnContentHover && isContentHovered);
  const isHoveredRef = useRef(isHovered);
  isHoveredRef.current = isHovered;

  const fade = useFadeVisibility(isHoveredRef);
  const { show, scheduleHide } = fade;

  // The content element belongs to MainContent, so hand it to the hook
  // directly instead of rendering a ref onto it. The hook releases the
  // previous element itself.
  useEffect(() => {
    if (!revealOnContentHover) return;
    contentHoverRef(scrollRef.current);
    return () => contentHoverRef(null);
  }, [scrollRef, contentHoverRef, revealOnContentHover]);

  // Keyed on the hover transition alone. updateThumb's identity changes with
  // the registered scroll target, and reacting to that here would re-run the
  // reveal on every target swap.
  const updateThumbRef = useRef(updateThumb);
  updateThumbRef.current = updateThumb;

  useEffect(() => {
    if (!isHovered) {
      scheduleHide();
      return;
    }
    updateThumbRef.current();
    show();
  }, [isHovered, show, scheduleHide]);

  return { ...fade, railHoverRef };
};

/**
 * Maps a thumb top offset within the wrapper to `scrollTop` on {@link el}.
 */
const scrollToThumbTop = (
  el: HTMLElement,
  thumbTop: number,
  thumbHeight: number,
): void => {
  const { clientHeight, scrollHeight } = el;
  const maxScroll = scrollHeight - clientHeight;
  if (maxScroll <= 0) return;
  const maxThumbTop = clientHeight - thumbHeight;
  if (maxThumbTop <= 0) return;
  const clamped = Math.min(Math.max(thumbTop, 0), maxThumbTop);
  el.scrollTop = (clamped / maxThumbTop) * maxScroll;
};

/** Right-edge rail driving the main content's vertical scroll. */
export const VerticalFadeScrollbar: React.FC<ScrollbarProps> = ({
  scrollRef,
  wrapperRef,
}) => {
  const [thumb, setThumb] = useState<VerticalThumbMetrics>({
    top: 0,
    height: 0,
  });
  const thumbRef = useRef(thumb);
  thumbRef.current = thumb;
  const grabOffsetYRef = useRef(0);

  const updateThumb = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { clientHeight, scrollHeight, scrollTop } = el;
    if (scrollHeight <= clientHeight) {
      setThumb({ top: 0, height: 0 });
      return;
    }
    const ratio = clientHeight / scrollHeight;
    const height = Math.max(
      clientHeight * ratio,
      SCROLLBAR_THUMB_MIN_LENGTH_PX,
    );
    const top =
      (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - height);
    setThumb({ top, height });
  }, [scrollRef]);

  const fade = useRailFade(scrollRef, updateThumb, false);
  const { visible, show, scheduleHide, railHoverRef } = fade;
  const { classes: thumbClasses } = useThumbStyles({ visible });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateThumb();
    const onScroll = (): void => {
      updateThumb();
      show();
      scheduleHide();
    };
    el.addEventListener('scroll', onScroll);
    const ro = new ResizeObserver(updateThumb);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [scrollRef, updateThumb, show, scheduleHide]);

  const handleGrab = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      grabOffsetYRef.current =
        e.clientY - wrapper.getBoundingClientRect().top - thumbRef.current.top;
    },
    [wrapperRef],
  );

  const handleDrag = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const el = scrollRef.current;
      const wrapper = wrapperRef.current;
      if (!el || !wrapper) return;
      const thumbTop =
        e.clientY -
        wrapper.getBoundingClientRect().top -
        grabOffsetYRef.current;
      scrollToThumbTop(el, thumbTop, thumbRef.current.height);
      updateThumb();
    },
    [scrollRef, wrapperRef, updateThumb],
  );

  const dragHandlers = useThumbDrag(fade, handleGrab, handleDrag);

  /** Jump scroll so the thumb centers on the click (track hit outside thumb). */
  const handleRailPointerDown = useCallback<
    PointerEventHandler<HTMLDivElement>
  >(
    (e) => {
      if (e.button !== 0) return;
      if (e.target !== e.currentTarget) return;
      const el = scrollRef.current;
      const wrapper = wrapperRef.current;
      if (!el || !wrapper) return;
      const { height } = thumbRef.current;
      const thumbTop =
        e.clientY - wrapper.getBoundingClientRect().top - height / 2;
      scrollToThumbTop(el, thumbTop, height);
      updateThumb();
      show();
    },
    [scrollRef, wrapperRef, updateThumb, show],
  );

  if (thumb.height <= 0) return null;

  return (
    <Box
      data-main-scrollbar="vertical"
      onPointerDown={handleRailPointerDown}
      ref={railHoverRef}
      sx={mergeSx(verticalRailSx, railSx)}
    >
      <Box
        data-main-scrollbar-thumb="vertical"
        className={thumbClasses.thumb}
        {...dragHandlers}
        sx={{
          position: 'absolute',
          right: 1,
          top: thumb.top,
          width: SCROLLBAR_THUMB_THICKNESS_PX,
          height: thumb.height,
        }}
      />
    </Box>
  );
};

/**
 * Bottom rail driving the horizontal scroller registered in
 * {@link getMainHorizontalScrollTarget} (the Import metadata strips), or an explicit target.
 */
export const HorizontalFadeScrollbar: React.FC<
  ScrollbarProps & {
    targetRef?: RefObject<HTMLElement | null>;
    revealOnContentHover?: boolean;
  }
> = ({ scrollRef, wrapperRef, targetRef, revealOnContentHover = true }) => {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [thumb, setThumb] = useState<HorizontalThumbMetrics>({
    left: 0,
    width: 0,
    trackLeft: 0,
  });
  const thumbRef = useRef(thumb);
  thumbRef.current = thumb;
  const grabOffsetXRef = useRef(0);

  const updateThumb = useCallback(() => {
    const rail = wrapperRef.current;
    if (!target || !rail) {
      setThumb({ left: 0, width: 0, trackLeft: 0 });
      return;
    }
    // Thumb lives in the metadata pane, not under the frozen name column.
    const trackLeft =
      target.getBoundingClientRect().left - rail.getBoundingClientRect().left;
    const metrics = horizontalScrollThumbMetrics(
      target.scrollLeft,
      target.clientWidth,
      target.scrollWidth,
      SCROLLBAR_THUMB_MIN_LENGTH_PX,
    );
    if (!metrics) {
      setThumb({ left: 0, width: 0, trackLeft });
      return;
    }
    setThumb({
      left: trackLeft + metrics.left,
      width: metrics.width,
      trackLeft,
    });
  }, [target, wrapperRef]);

  const fade = useRailFade(scrollRef, updateThumb, revealOnContentHover);
  const { visible, show, scheduleHide, railHoverRef } = fade;
  const { classes: thumbClasses } = useThumbStyles({ visible });

  useEffect(() => {
    if (targetRef) {
      setTarget(targetRef.current);
      return;
    }
    const syncTarget = (): void => {
      setTarget(getMainHorizontalScrollTarget());
    };
    syncTarget();
    return subscribeMainHorizontalScrollTarget(syncTarget);
  }, [targetRef]);

  useEffect(() => {
    if (!target) {
      setThumb({ left: 0, width: 0, trackLeft: 0 });
      return;
    }
    updateThumb();
    const onScroll = (): void => {
      updateThumb();
      show();
      scheduleHide();
    };
    target.addEventListener('scroll', onScroll);
    const ro = new ResizeObserver(updateThumb);
    ro.observe(target);
    if (target.firstElementChild) ro.observe(target.firstElementChild);
    const rail = wrapperRef.current;
    if (rail) ro.observe(rail);
    return () => {
      target.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [target, wrapperRef, updateThumb, show, scheduleHide]);

  const applyThumbLeft = useCallback(
    (clientX: number, grabOffsetX: number) => {
      const rail = wrapperRef.current;
      if (!target || !rail) return;
      const thumbLeft =
        clientX -
        rail.getBoundingClientRect().left -
        grabOffsetX -
        thumbRef.current.trackLeft;
      target.scrollLeft = scrollLeftFromThumbLeft(
        thumbLeft,
        thumbRef.current.width,
        target.clientWidth,
        target.scrollWidth,
      );
      updateThumb();
    },
    [target, wrapperRef, updateThumb],
  );

  const handleGrab = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const rail = wrapperRef.current;
      if (!rail) return;
      grabOffsetXRef.current =
        e.clientX - rail.getBoundingClientRect().left - thumbRef.current.left;
    },
    [wrapperRef],
  );

  const handleDrag = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      applyThumbLeft(e.clientX, grabOffsetXRef.current);
    },
    [applyThumbLeft],
  );

  const dragHandlers = useThumbDrag(fade, handleGrab, handleDrag);

  /** Jump scroll so the thumb centers on the click (track hit outside thumb). */
  const handleRailPointerDown = useCallback<
    PointerEventHandler<HTMLDivElement>
  >(
    (e) => {
      if (e.button !== 0) return;
      if (e.target !== e.currentTarget) return;
      applyThumbLeft(e.clientX, thumbRef.current.width / 2);
      show();
    },
    [applyThumbLeft, show],
  );

  if (!target) return null;

  return (
    <Box
      data-main-scrollbar="horizontal"
      onPointerDown={handleRailPointerDown}
      ref={railHoverRef}
      sx={mergeSx(horizontalRailSx, railSx)}
    >
      {thumb.width > 0 ? (
        <Box
          data-main-scrollbar-thumb="horizontal"
          className={thumbClasses.thumb}
          {...dragHandlers}
          sx={{
            position: 'absolute',
            bottom: 1,
            left: thumb.left,
            width: thumb.width,
            height: SCROLLBAR_THUMB_THICKNESS_PX,
          }}
        />
      ) : null}
    </Box>
  );
};

/**
 * Viewport-sized custom scrollbars for the main content: they fade when idle,
 * reappear on scroll or when hovering the right / bottom rail, and support
 * drag. Native scrollbars stay hidden so sticky headers keep full width.
 */
export const MainLayoutScrollbars: React.FC<ScrollbarProps> = ({
  scrollRef,
  wrapperRef,
}) => (
  <>
    <HorizontalFadeScrollbar scrollRef={scrollRef} wrapperRef={wrapperRef} />
    <VerticalFadeScrollbar scrollRef={scrollRef} wrapperRef={wrapperRef} />
  </>
);
